import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Text,
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronRight, FaSearch } from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../config";
import { getToken } from "../token";
import { FORUM, TRANSITION } from "../Theme/styles";
import TaskModal from "../Components/TaskModal";
import { WZOR_OBRAZKA } from "../Components/TaskDescription";
import { eventInfo } from "../events";
import { findCategory } from "../categories";
import { ALL_COLUMNS } from "../columns";
import { priorityName } from "../priorities";
import { formatDate } from "../dates";
import { Chip } from "./Activity";

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const LOGS_LIMIT = 1000;

const FILTERS = [
  {
    key: "removals",
    name: "Usunięcia",
    kolor: "#E06C6C",
    events: ["task_deleted", "item_deleted", "assignment_removed"],
  },
  { key: "edits", name: "Edycje", events: ["task_edited", "item_edited"] },
];

const WITH_SNAPSHOT = FILTERS.flatMap((f) => f.events);

const FIELD_NAMES = {
  title: "Tytuł",
  description: "Opis",
  piority: "Priorytet",
  deadline: "Termin",
  details: "Szczegóły",
};

const RED = "#E06C6C";
const GREEN = "#4FB477";

// datetime-local takes local time with no seconds and no zone.
const toInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatTime = (value) =>
  new Date(value).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const imagesIn = (text) => String(text || "").match(WZOR_OBRAZKA) || [];

const categoryName = (key) => findCategory(key)?.name || key;

const readableDetails = (w) => {
  if (!w.details) return w.details;
  if (w.event === "assignment_added" || w.event === "assignment_removed") {
    return categoryName(w.details);
  }
  if (w.event === "team_changed") {
    return w.details.split(" -> ").map(categoryName).join(" → ");
  }
  return w.details;
};

const Logs = () => {
  const [from, setOd] = useState(() => toInput(new Date(Date.now() - DAY_MS)));
  const [to, setDo] = useState("");
  const [refresh, setOdswiez] = useState(0);
  const [entries, setWpisy] = useState([]);
  const [people, setOsoby] = useState([]);
  const [search, setSzukaj] = useState("");
  const [filter, setFiltr] = useState(null);
  const [loading, setLaduje] = useState(true);
  const [error, setBlad] = useState(null);
  const [expanded, setRozwiniety] = useState(null);
  const [openTask, setOtwarte] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/users`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((r) => setOsoby(r.data || []))
      .catch(() => setOsoby([]));
  }, []);

  useEffect(() => {
    let current = true;
    const params = {};
    if (from) params.from = new Date(from).toISOString();
    // "Do 00:45" should still include 00:45:59.
    if (to) params.to = new Date(new Date(to).getTime() + MINUTE_MS - 1).toISOString();

    setLaduje(true);
    setBlad(null);
    axios
      .get(`${API_URL}/logs`, {
        params,
        withCredentials: true,
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((r) => current && setWpisy(r.data || []))
      .catch((e) => {
        if (!current) return;
        setWpisy([]);
        setBlad(
          e?.response?.status === 403
            ? "Logi widzi tylko Administracja RCON."
            : "Nie udało się pobrać logów."
        );
      })
      .finally(() => current && setLaduje(false));

    return () => {
      current = false;
    };
  }, [from, to, refresh]);

  const nick = (id) =>
    people.find((o) => String(o.id) === String(id))?.nickname || id;

  const visible = useMemo(() => {
    const events = FILTERS.find((f) => f.key === filter)?.events;
    const query = search.trim().toLowerCase();
    return entries.filter((w) => {
      if (events && !events.includes(w.event)) return false;
      if (!query) return true;
      return [
        w.task_title,
        w.details,
        w.actor_name,
        eventInfo(w.event).name,
        `#${w.task_id}`,
        w.snapshot ? JSON.stringify(w.snapshot) : "",
      ].some((text) => String(text || "").toLowerCase().includes(query));
    });
  }, [entries, filter, search]);

  const policz = (events) => entries.filter((w) => events.includes(w.event)).length;

  const minutes = (value) => formatTime(new Date(value)).slice(0, -3);
  const range = from
    ? `${minutes(from)} – ${to ? minutes(to) : "teraz"}`
    : `ostatnie 24 h${to ? " do " + minutes(to) : ""}`;

  return (
    <Flex direction={"column"} h={"100%"} minH={0} gap={4} w={"100%"}>
      <Flex direction={"column"} gap={0.5}>
        <Text
          fontSize={"11px"}
          fontWeight={"500"}
          letterSpacing={"0.08em"}
          textTransform={"uppercase"}
          color={FORUM.tekstPrzygaszony}
        >
          Pełna historia zmian
        </Text>
        <Text fontSize={"xl"} fontWeight={"600"} lineHeight={"1.1"} color={FORUM.tekstJasny}>
          Logi
        </Text>
      </Flex>

      <Flex gap={2} wrap={"wrap"} align={"center"} flexShrink={0}>
        <RangeInput label={"Od"} value={from} onChange={setOd} />
        <RangeInput label={"Do"} value={to} onChange={setDo} />
        <InputGroup size={"sm"} w={{ base: "100%", md: "260px" }}>
          <InputLeftElement pointerEvents={"none"} h={"32px"}>
            <FaSearch color={FORUM.tekstPrzygaszony} size={12} />
          </InputLeftElement>
          <Input
            h={"32px"}
            placeholder={"Zadanie, osoba, treść…"}
            value={search}
            onChange={(e) => setSzukaj(e.target.value)}
          />
        </InputGroup>
        <Button size={"sm"} variant={"drugorzedny"} onClick={() => setOdswiez((n) => n + 1)}>
          Odśwież
        </Button>
      </Flex>

      <Flex gap={2} wrap={"wrap"} align={"center"} flexShrink={0}>
        <Chip name={"Wszystko"} ile={entries.length} selected={!filter} onClick={() => setFiltr(null)} />
        {FILTERS.map((f) => (
          <Chip
            key={f.key}
            name={f.name}
            ile={policz(f.events)}
            kolor={f.kolor}
            selected={filter === f.key}
            onClick={() => setFiltr(filter === f.key ? null : f.key)}
          />
        ))}
        <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony}>
          {range}
          {entries.length >= LOGS_LIMIT &&
            ` · pokazuję ${LOGS_LIMIT} najnowszych, zawęź zakres, żeby zobaczyć starsze`}
        </Text>
      </Flex>

      <Box flex={1} minH={0} overflowY={"auto"}>
        {loading && (
          <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
            Wczytuję…
          </Text>
        )}
        {!loading && error && (
          <Text fontSize={"sm"} color={RED}>
            {error}
          </Text>
        )}
        {!loading && !error && visible.length === 0 && (
          <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
            W tym zakresie nic się nie działo.
          </Text>
        )}

        <Flex direction={"column"} gap={1}>
          {!loading &&
            visible.map((w) => {
              const zdarzenie = eventInfo(w.event);
              const open = expanded === w.id;
              return (
                <Box
                  key={w.id}
                  bg={open ? FORUM.panelJasny : FORUM.panel}
                  border={"1px solid"}
                  borderColor={FORUM.obramowanie}
                  borderRadius={"md"}
                  transition={TRANSITION}
                >
                  <Flex
                    align={"center"}
                    gap={3}
                    px={3}
                    py={2}
                    cursor={"pointer"}
                    _hover={{ bg: FORUM.panelJasny }}
                    onClick={() => setRozwiniety(open ? null : w.id)}
                  >
                    <Icon
                      as={open ? FaChevronDown : FaChevronRight}
                      boxSize={"10px"}
                      color={FORUM.tekstPrzygaszony}
                      flexShrink={0}
                    />
                    <Text
                      fontSize={"11px"}
                      color={FORUM.tekstPrzygaszony}
                      minW={"112px"}
                      whiteSpace={"nowrap"}
                      fontVariantNumeric={"tabular-nums"}
                      display={{ base: "none", md: "block" }}
                    >
                      {formatTime(w.created_at)}
                    </Text>
                    <Text
                      fontSize={"10px"}
                      fontWeight={"600"}
                      color={zdarzenie.kolor}
                      bg={FORUM.plakietka}
                      borderRadius={"full"}
                      px={2}
                      py={"1px"}
                      minW={"110px"}
                      textAlign={"center"}
                      whiteSpace={"nowrap"}
                      display={{ base: "none", md: "block" }}
                    >
                      {zdarzenie.name}
                    </Text>
                    <Box flex={1} minW={0}>
                      <Text fontSize={"sm"} color={FORUM.tekst} noOfLines={1}>
                        {w.task_title || `zadanie #${w.task_id}`}
                      </Text>
                      <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} noOfLines={1}>
                        <Text as={"span"} display={{ md: "none" }}>
                          {formatTime(w.created_at)} ·{" "}
                          <Text as={"span"} color={zdarzenie.kolor}>
                            {zdarzenie.name}
                          </Text>
                          {w.details && " · "}
                        </Text>
                        {readableDetails(w)}
                      </Text>
                    </Box>
                    <Text
                      fontSize={"11px"}
                      color={w.actor_id ? "#6C99FF" : FORUM.tekstPrzygaszony}
                      whiteSpace={"nowrap"}
                    >
                      {w.actor_name}
                    </Text>
                  </Flex>

                  {open && (
                    <Flex
                      direction={"column"}
                      gap={3}
                      px={3}
                      pb={3}
                      pt={2}
                      borderTop={"1px solid"}
                      borderColor={FORUM.obramowanie}
                    >
                      <Snapshot entry={w} nick={nick} />
                      <Flex gap={3} align={"center"} wrap={"wrap"}>
                        <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony}>
                          {new Date(w.created_at).toLocaleString("pl-PL")} · zadanie #{w.task_id}
                          {w.actor_id && ` · ID osoby ${w.actor_id}`}
                        </Text>
                        {w.event !== "task_deleted" && (
                          <Button size={"xs"} variant={"drugorzedny"} onClick={() => setOtwarte(w.task_id)}>
                            Otwórz zadanie
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  )}
                </Box>
              );
            })}
        </Flex>
      </Box>

      {openTask && (
        <TaskModal
          isOpen={Boolean(openTask)}
          onClose={() => setOtwarte(null)}
          taskId={openTask}
          peoples={people}
        />
      )}
    </Flex>
  );
};

const RangeInput = ({ label, value, onChange }) => (
  <Flex align={"center"} gap={2}>
    <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
      {label}
    </Text>
    <Input
      type={"datetime-local"}
      size={"sm"}
      h={"32px"}
      w={"auto"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ colorScheme: "dark" }}
    />
  </Flex>
);

const Snapshot = ({ entry, nick }) => {
  const s = entry.snapshot;

  if (!s) {
    if (!WITH_SNAPSHOT.includes(entry.event)) return null;
    return (
      <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
        Ten wpis powstał, zanim logi zaczęły zapamiętywać treść — zostało tylko to, co widać wyżej.
      </Text>
    );
  }

  if (s.before && s.after) {
    return (
      <Flex direction={"column"} gap={3}>
        {Object.keys(s.before).map((key) => (
          <FieldChange key={key} field={key} before={s.before[key]} after={s.after[key]} />
        ))}
      </Flex>
    );
  }

  if (entry.event === "task_deleted") {
    return (
      <Flex direction={"column"} gap={3}>
        <Flex direction={"column"} gap={0.5}>
          <Text fontSize={"md"} fontWeight={"600"} color={FORUM.tekstJasny}>
            {s.title}
          </Text>
          <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony}>
            Priorytet: {priorityName(s.piority)}
            {s.deadline && ` · termin ${formatDate(s.deadline)}`}
            {s.created_by && ` · autor ${nick(s.created_by)}`}
          </Text>
        </Flex>
        <Box fontSize={"sm"} color={FORUM.tekst}>
          <Description text={s.description} />
        </Box>
        {(s.assignments || []).map((a) => (
          <AssignmentView key={a.id} assignment={a} nick={nick} />
        ))}
      </Flex>
    );
  }

  if (entry.event === "assignment_removed") {
    return <AssignmentView assignment={s} nick={nick} />;
  }

  if (entry.event === "item_deleted") {
    return <ItemView item={s} nick={nick} />;
  }

  return (
    <Box
      as={"pre"}
      fontSize={"11px"}
      color={FORUM.tekstPrzygaszony}
      whiteSpace={"pre-wrap"}
      wordBreak={"break-word"}
    >
      {JSON.stringify(s, null, 2)}
    </Box>
  );
};

const FieldChange = ({ field, before, after }) => {
  const removedImages =
    field === "description" || field === "details"
      ? imagesIn(before).filter((url) => !imagesIn(after).includes(url))
      : [];

  return (
    <Flex direction={"column"} gap={1.5}>
      <Text fontSize={"11px"} fontWeight={"600"} color={FORUM.tekstPrzygaszony} textTransform={"uppercase"}>
        {FIELD_NAMES[field] || field}
      </Text>
      <Version label={"Było"} kolor={RED} field={field} value={before} removed={removedImages} />
      <Version label={"Jest"} kolor={GREEN} field={field} value={after} />
      {removedImages.length > 0 && (
        <Flex direction={"column"} gap={1}>
          <Text fontSize={"11px"} color={RED}>
            Usunięte zdjęcia (pliki dalej leżą pod tymi adresami):
          </Text>
          {removedImages.map((url) => (
            <Link key={url} href={url} isExternal fontSize={"sm"} color={"#6C99FF"} wordBreak={"break-all"}>
              {url}
            </Link>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const Version = ({ label, kolor, field, value, removed }) => {
  let content;
  if (field === "description" || field === "details") {
    content = <Description text={value} removed={removed} />;
  } else if (field === "piority") content = priorityName(value);
  else if (field === "deadline") content = value ? formatDate(value) : "brak";
  else content = value || "—";

  return (
    <Box
      borderLeft={"2px solid"}
      borderColor={kolor}
      pl={3}
      fontSize={"sm"}
      color={FORUM.tekst}
      whiteSpace={"pre-wrap"}
      wordBreak={"break-word"}
    >
      <Text as={"span"} fontSize={"11px"} color={kolor} display={"block"} mb={0.5}>
        {label}
      </Text>
      {content}
    </Box>
  );
};

// Compact on purpose: a log shows what changed, the task modal is for reading.
const Description = ({ text, removed = [] }) => {
  const images = imagesIn(text);
  const rest = String(text || "").replace(WZOR_OBRAZKA, "").trim();
  return (
    <Box>
      <Text color={rest ? FORUM.tekst : FORUM.tekstPrzygaszony} whiteSpace={"pre-wrap"}>
        {rest || "pusto"}
      </Text>
      {images.length > 0 && (
        <Flex gap={2} wrap={"wrap"} mt={2}>
          {images.map((url, i) => (
            <Link key={`${url}-${i}`} href={url} isExternal>
              <Image
                src={url}
                alt={"Załącznik"}
                h={"72px"}
                maxW={"140px"}
                objectFit={"cover"}
                borderRadius={"md"}
                border={"2px solid"}
                borderColor={removed.includes(url) ? RED : "transparent"}
              />
            </Link>
          ))}
        </Flex>
      )}
    </Box>
  );
};

const AssignmentView = ({ assignment, nick }) => {
  const status = ALL_COLUMNS.find((k) => k.key === assignment.status)?.name || assignment.status;
  return (
    <Box bg={FORUM.tlo} border={"1px solid"} borderColor={FORUM.obramowanie} borderRadius={"md"} p={3}>
      <Text fontSize={"sm"} fontWeight={"600"} color={FORUM.tekstJasny}>
        {categoryName(assignment.team)}
        {assignment.assigned_to && ` · ${nick(assignment.assigned_to)}`}
      </Text>
      <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} mb={2}>
        {status}
        {assignment.deadline && ` · termin ${formatDate(assignment.deadline)}`}
        {assignment.claimed_by && ` · wziął ${nick(assignment.claimed_by)}`}
      </Text>
      <Flex direction={"column"} gap={1.5}>
        {(assignment.items || []).length === 0 && (
          <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
            Bez pozycji.
          </Text>
        )}
        {(assignment.items || []).map((item) => (
          <ItemView key={item.id} item={item} nick={nick} />
        ))}
      </Flex>
    </Box>
  );
};

const ItemView = ({ item, nick }) => (
  <Flex gap={2} align={"flex-start"}>
    <Text fontSize={"sm"} color={item.done ? GREEN : FORUM.tekstPrzygaszony} lineHeight={"1.4"}>
      {item.done ? "✓" : "○"}
    </Text>
    <Box minW={0}>
      <Text fontSize={"sm"} color={FORUM.tekst} whiteSpace={"pre-wrap"} wordBreak={"break-word"}>
        {item.description}
      </Text>
      {item.details && (
        <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} whiteSpace={"pre-wrap"} wordBreak={"break-word"}>
          {item.details}
        </Text>
      )}
      {item.done_by && (
        <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony}>
          odhaczył {nick(item.done_by)}
        </Text>
      )}
    </Box>
  </Flex>
);

export default Logs;
